import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import { TABLES } from '../../lib/constants';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiDollarSign, FiUsers, FiPackage, FiTrendingUp } = FiIcons;

const StatCard = ({ title, value, icon, color }) => (
  <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm hover:shadow-md transition-all">
    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${color} text-white shadow-lg mb-4`}>
      <SafeIcon icon={icon} className="text-xl" />
    </div>
    <h3 className="text-gray-500 text-[10px] font-black uppercase tracking-widest">{title}</h3>
    <p className="text-3xl font-black text-gray-900 mt-1">{value}</p>
  </div>
);

const MasterDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({ pilots: 0, items: 0, totalPaid: 0, pending: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) loadStats();
  }, [user]);

  const loadStats = async () => {
    setLoading(true);
    try {
      const { count: pilotsCount } = await supabase
        .from(TABLES.PILOT_MASTERS)
        .select('*', { count: 'exact', head: true })
        .eq('master_id', user.id)
        .eq('status', 'active');

      const { count: itemsCount } = await supabase
        .from(TABLES.ITEMS)
        .select('*', { count: 'exact', head: true })
        .eq('master_id', user.id);

      const { data: logs } = await supabase
        .from(TABLES.LOGS)
        .select('value_at_log, status')
        .eq('master_id', user.id);

      const totalPaid = logs?.filter(l => l.status === 'paid').reduce((sum, l) => sum + (Number(l.value_at_log) || 0), 0) || 0;
      const pending = logs?.filter(l => l.status === 'unpaid').reduce((sum, l) => sum + (Number(l.value_at_log) || 0), 0) || 0;

      setStats({ pilots: pilotsCount || 0, items: itemsCount || 0, totalPaid, pending });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="animate-pulse h-64 bg-gray-100 rounded-[3rem]" />;

  return (
    <div className="space-y-10">
      <header>
        <h2 className="text-4xl font-black text-gray-900 tracking-tight">Master Overview</h2>
        <p className="text-gray-500 font-bold mt-2">Managing team performance for @{user?.username}</p>
      </header>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard title="Active Pilots" value={stats.pilots} icon={FiUsers} color="bg-blue-600" />
        <StatCard title="Drops Catalog" value={stats.items} icon={FiPackage} color="bg-indigo-600" />
        <StatCard title="Pending Payouts" value={`₱${stats.pending.toLocaleString()}`} icon={FiDollarSign} color="bg-amber-600" />
        <StatCard title="Total Settled" value={`₱${stats.totalPaid.toLocaleString()}`} icon={FiTrendingUp} color="bg-emerald-600" />
      </div>
    </div>
  );
};

export default MasterDashboard;